import { startCase } from 'lodash';
import React, { useEffect, useMemo } from 'react';
import styled from 'styled-components';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormHelperText
} from '@material-ui/core';
import {
  State,
  StateEvent,
  StateTransitionCondition,
  WorkflowParams
} from '@sierralabs/workflow';

import { Task, TaskEntity } from '../../../tasks/Task.interface';
import { RequireFieldsParams, TaskContext } from '../../../tasks/Task.machine';
import FieldSelector from '../../components/FieldSelector';
import { useWorkflowContext } from '../../Workflow.context';
import { useWorkflowFields } from '../../Workflow.hooks';
import { WorkflowTaskTypeEntity } from '../../Workflow.interface';
import {
  TransitionConditionConfiguratorProps,
  TransitionConditionDisplayProps,
  TransitionConditionPlugin
} from '../TransitionConditionPlugin';

// NOTE: workaround for hardcoded `*.fields.ts` as
// they sometimes have different keys for actual values
function getRequiredFieldValue(
  field: string,
  options: { task: Task; taskEntity: TaskEntity }
) {
  const { task, taskEntity } = options;
  switch (field) {
    case 'taskFiles':
      return task.files;
    case 'attachment':
      return task.attachments;
    case 'assignTask':
      return task.user;
    default:
      return taskEntity[field as keyof TaskEntity];
  }
}

function validateRequiredField(field: any) {
  switch (typeof field) {
    case 'string':
      return field.length > 0;
    case 'number':
      return isFinite(field);
    case 'boolean':
      return true;
    case 'object':
      return Array.isArray(field) ? field.length > 0 : !!field;
  }
  return false;
}

const Styled = {
  FormHelperText: styled(FormHelperText)`
    padding-left: 8px;
  `
};

const RequiredFieldsConfigurator: React.FC<
  TransitionConditionConfiguratorProps
> = ({ taskType, event, condition, onChange }) => {
  if (!condition.params) {
    condition.params = {};
  }
  if (!Array.isArray(condition.params.requiredFields)) {
    condition.params.requiredFields = [];
  }
  return (
    <>
      <Box mt={2}>
        <FieldSelector
          type={taskType}
          fields={condition.params.requiredFields}
          onChange={(fields: string[]) => {
            condition.params!.requiredFields = fields;
            onChange?.({ ...condition });
          }}
        />
        <Styled.FormHelperText>
          Checked fields will be required for this transition.
        </Styled.FormHelperText>
      </Box>
    </>
  );
};

const StyledUl = styled.ul`
  margin: 0;
  padding-left: 16px;
  li:not(:last-child) {
    margin-bottom: 4px;
  }
`;

interface RequiredFieldsDialogProps {
  task: Task;
  open: boolean;
  taskEntity: TaskEntity;
  event: StateEvent | undefined;
  onClose?: () => void;
}

const RequiredFieldsDialog: React.FC<RequiredFieldsDialogProps> = props => {
  const { open, event, task, taskEntity, onClose } = props;
  const { workflows, getWorkflow } = useWorkflowContext();
  const workflowItem = workflows.find(workflow => {
    // check with workflowId if no taskType
    return task.taskTypeName
      ? workflow.taskTypeName === task.taskTypeName
      : workflow.id === task.workflowId;
  });
  const type = workflowItem?.taskType?.targetEntity || '';
  const { labels } = useWorkflowFields(type as WorkflowTaskTypeEntity);

  const requiredFields = useMemo(() => {
    const requiredFields: string[] = [];
    for (const transition of event?.transitions || []) {
      for (const condition of transition.conditions || []) {
        const params = condition.params as RequireFieldsParams | undefined;
        if (
          condition.type === RequiredFieldsPlugin.type &&
          params &&
          Array.isArray(params.requiredFields)
        ) {
          requiredFields.push(...params.requiredFields);
        }
      }
    }
    return requiredFields;
  }, [event]);

  const invalidFields = useMemo(() => {
    return requiredFields.filter(field => {
      const value = getRequiredFieldValue(field, { task, taskEntity });
      return !validateRequiredField(value);
    });
  }, [requiredFields, task, taskEntity]);

  // fetch workflow in case it's not yet fetched
  useEffect(() => {
    getWorkflow(task);
  }, [task, getWorkflow]);

  return (
    <Dialog fullWidth open={open} maxWidth='xs' onClose={onClose}>
      <DialogTitle>Required Fields</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {invalidFields.length > 0
            ? 'The following fields are required to transition this item:'
            : 'All required fields passed.'}
        </DialogContentText>
        {invalidFields.length > 0 && (
          <StyledUl>
            {invalidFields.map((field, index) => {
              const label = labels[field as keyof typeof labels];
              return (
                <li key={`${field}-${index}`}>{label || startCase(field)}</li>
              );
            })}
          </StyledUl>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const RequiredFieldsDisplay: React.FC<TransitionConditionDisplayProps> = ({
  condition
}) => {
  return <>Required Fields</>;
};

export const RequiredFieldsPlugin: TransitionConditionPlugin = {
  type: 'require-fields',
  label: 'Required Fields',
  configurator: RequiredFieldsConfigurator,
  display: RequiredFieldsDisplay,
  validate: (condition: Partial<StateTransitionCondition>) => {
    return true;
  },
  guard: (state: State<TaskContext>, params: WorkflowParams, render) => {
    console.log('guards: require-fields', state, params);
    if (
      state.isSimulation ||
      state.context.skipTransitionConditionPlugins?.includes(
        RequiredFieldsPlugin.type
      )
    ) {
      return true;
    }
    const { event, task, taskEntity } = state.context;
    if (!taskEntity) {
      console.error('No Task Entity found for "require-fields" guard.');
      return false;
    }
    const result = params.requiredFields.every((requiredField: string) => {
      // field should be valid
      const value = getRequiredFieldValue(requiredField, { task, taskEntity });
      return validateRequiredField(value);
    });
    // skip render if validation has already passed
    if (result || !render) {
      return result;
    }
    return new Promise(resolve => {
      render(
        <RequiredFieldsDialog
          open
          task={task}
          event={event}
          taskEntity={taskEntity}
          onClose={() => {
            render?.(undefined);
            resolve(result);
          }}
        />
      );
    });
  }
};
